import { apiClient, ApiResponse } from './api';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  marketingConsent?: boolean;
  referralCode?: string;
}

export interface AuthResponse {
  user: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    profileCompleted: boolean;
    hasProfile: boolean;
    marketingConsent?: boolean;
    marketingConsentAsked?: boolean;
    createdAt: string;
    tier?: string;
    subscriptionStatus?: string | null;
    subscriptionPeriodEnd?: string | null;
  };
  tokens?: {
    accessToken: string;
    refreshToken: string;
    expiresIn?: number;
  };
  // Flat token fields for backward compatibility
  accessToken?: string;
  refreshToken?: string;
}

export interface OAuthCredentials {
  code: string;
  redirectUri?: string;
}

export const authService = {
  /**
   * Login with email and password
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await apiClient.post<ApiResponse<AuthResponse>>(
      '/api/v1/auth/login',
      credentials
    );
    return response.data.data;
  },

  /**
   * Register new user with email and password
   */
  async register(data: RegisterData): Promise<AuthResponse> {
    // Backend expects { email, password, name, marketingConsent?, referralCode? }
    const backendData: Record<string, unknown> = {
      email: data.email,
      password: data.password,
      name: [data.firstName, data.lastName].filter(Boolean).join(' ') || 'User',
    };
    if (data.marketingConsent !== undefined) {
      backendData.marketingConsent = data.marketingConsent;
    }
    if (data.referralCode) {
      backendData.referralCode = data.referralCode;
    }
    const response = await apiClient.post<ApiResponse<AuthResponse>>('/api/v1/auth/register', backendData);
    return response.data.data;
  },

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) {
      await apiClient.post('/api/v1/auth/logout', { refreshToken });
    }
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  },

  /**
   * Get current authenticated user
   */
  async getCurrentUser() {
    const response = await apiClient.get<ApiResponse<AuthResponse['user']>>('/api/v1/auth/me');
    return response.data.data;
  },

  /**
   * Refresh access token
   */
  async refreshToken(refreshToken: string): Promise<{ accessToken: string; refreshToken: string }> {
    const response = await apiClient.post<
      ApiResponse<{ accessToken: string; refreshToken: string }>
    >('/api/v1/auth/refresh', { refreshToken });
    return response.data.data;
  },

  /**
   * Login with Google OAuth
   */
  async loginWithGoogle(credentials: OAuthCredentials): Promise<AuthResponse> {
    const response = await apiClient.post<ApiResponse<AuthResponse>>(
      '/api/v1/auth/oauth/google',
      credentials
    );
    return response.data.data;
  },

  /**
   * Login with LinkedIn OAuth
   */
  async loginWithLinkedIn(credentials: OAuthCredentials): Promise<AuthResponse> {
    const response = await apiClient.post<ApiResponse<AuthResponse>>(
      '/api/v1/auth/oauth/linkedin',
      credentials
    );
    return response.data.data;
  },
};
